/**
 * MSR (Master Stock Report)
 *
 * Excel-like report showing comprehensive inventory data with:
 * - Rows grouped by product category (orange/pink headers)
 * - Sub-rows per geography (US, CA, SG, UK, EU, AU) with Total row per SKU
 * - Run rates with bundle explosion (D7, D14, D30 daily averages)
 * - Revenue share calculated with COGS-based combo distribution
 * - Products sorted by revenue share % descending
 * - Conditional formatting (negative values in red, totals bold with yellow bg)
 * - Export to Excel
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Popover,
  Divider,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Refresh,
  FileDownload,
  Business,
  Search,
  ArrowUpward,
  ArrowDownward,
  UnfoldMore,
  Save,
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import { useSearchParams } from 'react-router-dom';
import { useBrand } from '../../context/BrandContext';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useVirtualizer } from '@tanstack/react-virtual';
import StockBreakdownDialog from '../inventory/StockBreakdownDialog';

// Geography order as defined in backend
const GEOGRAPHY_ORDER = ['US', 'CA', 'SG', 'UK', 'EU', 'AU'];

// Calculation descriptions for clickable cells
const CALCULATION_INFO = {
  revenue_share: {
    title: 'Revenue Share %',
    formula: '(SKU Revenue / Total Brand Revenue) × 100',
    description: 'Percentage of total brand revenue attributed to this SKU in the selected geography. For bundles, revenue is distributed based on COGS proportion of each component.',
  },
  d7_daily: {
    title: 'D7 Daily Run Rate',
    formula: 'Units Sold (Last 7 Days) / 7',
    description: 'Average daily units sold over the last 7 days. For bundles, this includes exploded component quantities.',
  },
  d14_daily: {
    title: 'D14 Daily Run Rate',
    formula: 'Units Sold (Last 14 Days) / 14',
    description: 'Average daily units sold over the last 14 days. For bundles, this includes exploded component quantities.',
  },
  d30_daily: {
    title: 'D30 Daily Run Rate',
    formula: 'Units Sold (Last 30 Days) / 30',
    description: 'Average daily units sold over the last 30 days. For bundles, this includes exploded component quantities.',
  },
  planning_daily_velocity: {
    title: 'Planning Velocity',
    formula: 'Adjusted velocity signal for planning',
    description: 'Planning velocity calculated from the adjusted velocity signal. This is shown beside D30 so stockout-adjusted demand can be compared with observed 30-day sales.',
  },
  d_max: {
    title: 'D Max (Maximum Run Rate)',
    formula: 'MAX(D7 Daily, D14 Daily, D30 Daily)',
    description: 'The highest of the three run rate periods. Used to estimate peak demand for inventory planning.',
  },
  rod_l30: {
    title: 'Run Out Days (ROD L30)',
    formula: 'Stock on Hand / D30 Daily Rate',
    description: 'Estimated number of days until stock runs out based on the 30-day run rate. Shows N/A if no sales in last 30 days.',
  },
  main_branch_soh: {
    title: 'Stock on Hand (SOH)',
    formula: 'Sum of all warehouse stock in geography',
    description: 'Total units currently in stock across all warehouses in this geography.',
  },
  main_branch_available: {
    title: 'Stocks Available',
    formula: 'MAX(0, SOH - Reserved - Expiring)',
    description: 'Available stock for sale. Calculated as SOH minus retailer-blocked stock and stock expiring within 6 months. Shows 0 if SOH is negative.',
  },
  amazon_inventory: {
    title: 'Amazon FBA Inventory',
    formula: 'Amazon FBA fulfillable quantity',
    description: 'Units currently available in Amazon FBA fulfillment centers for this geography.',
  },
  pct_soh_region: {
    title: '% SOH by Region',
    formula: '(Region SOH / Total SKU SOH) × 100',
    description: 'Percentage of total SKU stock held in this specific geography/region.',
  },
  first_check_date: {
    title: 'First Check Date',
    formula: 'Earliest physical count date',
    description: 'The date of the first physical inventory count/check for this SKU in this geography.',
  },
  first_check_qty: {
    title: 'First Check Qty',
    formula: 'Quantity at first physical count',
    description: 'The inventory quantity recorded at the first physical count for this SKU in this geography.',
  },
  last_check_date: {
    title: 'Last Check Date',
    formula: 'Most recent physical count date',
    description: 'The date of the most recent physical inventory count/check for this SKU in this geography.',
  },
  last_check_qty: {
    title: 'Last Check Qty',
    formula: 'Quantity at last physical count',
    description: 'The inventory quantity recorded at the most recent physical count for this SKU in this geography.',
  },
  total_counts: {
    title: 'Total Physical Counts',
    formula: 'Count of distinct physical stock counts',
    description: 'The total number of physical inventory counts performed for this SKU in this geography.',
  },
  incoming_transfer: {
    title: 'Incoming Transfers (In Transit)',
    formula: 'Sum of warehouse transfers with status "in_transit"',
    description: 'Stock currently in transit from warehouse transfers. These are shipments that have been sent but not yet received.',
  },
  incoming_po: {
    title: 'Incoming Purchase Orders',
    formula: 'Sum of approved/sent POs not yet received',
    description: 'Stock expected from purchase orders that are approved or sent to suppliers but not yet received.',
  },
  proposed_transfer: {
    title: 'Proposed Transfer',
    formula: 'User-entered transfer quantity',
    description: 'Manually entered proposed stock transfer. Positive = stock coming in, Negative = stock going out. Used for "what-if" planning.',
  },
  soh_after_transfer: {
    title: 'SOH After Transfer',
    formula: 'SOH + Incoming Transfers + Incoming PO + Proposed Transfer',
    description: 'Projected stock on hand after all incoming stock and proposed transfers are applied.',
  },
  rod_after_transfer: {
    title: 'ROD After Transfer',
    formula: 'SOH After Transfer / D Max',
    description: 'Run-out days based on projected stock after transfers, using D Max (peak demand) rate.',
  },
  monthly_avg_rate: {
    title: '3-Month Avg Monthly Rate',
    formula: 'Sum(Units Sold over 90 days) / 3',
    description: 'Average monthly consumption rate calculated over the last 90 days (3 months). Used for inventory simulation projections.',
  },
  expiry_alert: {
    title: 'Expiry Alert',
    formula: 'Based on earliest batch expiry date',
    description: 'Alert level based on the earliest expiring batch: <6 Months (critical), 7-12 Months (warning), 13-18 Months (info).',
  },
  simulation_without_proposal: {
    title: 'Simulated Inventory (Without Proposal)',
    formula: 'Starting SOH + Incoming - Monthly Consumption - Expiring',
    description: '12-month inventory projection without proposed transfers. Shows end-of-month stock levels based on run rate and expiry.',
  },
  simulation_with_proposal: {
    title: 'Simulated Inventory (With Proposal)',
    formula: 'Starting SOH + Incoming + Proposed - Monthly Consumption - Expiring',
    description: '12-month inventory projection including proposed transfers. Shows end-of-month stock levels after proposed changes.',
  },
  monthly_expiry: {
    title: 'Monthly Expiry',
    formula: 'Sum of batch quantities expiring in each month',
    description: 'Quantity of stock expiring each month based on batch expiry dates from inventory.',
  },
};

const InventoryRunRateReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedBrand, brands, selectBrand } = useBrand();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [columnSizing, setColumnSizing] = useState({});
  const [hideZeroSkus, setHideZeroSkus] = useState(true);
  const [calcPopover, setCalcPopover] = useState({ anchorEl: null, field: null, row: null });
  // Stock breakdown dialog state
  const [stockBreakdownDialog, setStockBreakdownDialog] = useState({
    open: false,
    sku: '',
    warehouseCode: '',
    productName: '',
  });
  // Track edited proposed transfers: Map of `${sku}||${geography}` -> { value, warehouse_code }
  const [editedTransfers, setEditedTransfers] = useState({});

  // Ref for virtualized table container
  const tableContainerRef = useRef(null);

  // Read brand from URL parameter and select it when brands are loaded
  useEffect(() => {
    const brandIdFromUrl = searchParams.get('brand');
    if (brandIdFromUrl && brands.length > 0) {
      const brandFromUrl = brands.find(b => String(b.id) === String(brandIdFromUrl));
      if (brandFromUrl && (!selectedBrand || String(selectedBrand.id) !== String(brandIdFromUrl))) {
        selectBrand(brandFromUrl);
      }
    }
  }, [searchParams, brands, selectedBrand, selectBrand]);

  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    const brand = brands.find(b => b.id === brandId);
    if (brand) {
      selectBrand(brand);
      // Update URL to reflect brand change
      setSearchParams({ brand: brandId });
    }
  };

  const fetchReportData = useCallback(async () => {
    if (!selectedBrand?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`/api/inventory-master/flat`, {
        params: { brand_id: selectedBrand.id }
      });
      // API returns { success: true, data: { ... } }
      setReportData(response.data.data || response.data);
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError(err.response?.data?.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.id]);

  useEffect(() => {
    if (selectedBrand) {
      fetchReportData();
      setEditedTransfers({}); // Clear edits when brand changes
    }
  }, [selectedBrand, fetchReportData]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return Object.keys(editedTransfers).length > 0;
  }, [editedTransfers]);

  // Handle proposed transfer edit
  const handleTransferEdit = useCallback((sku, geography, warehouseCode, value) => {
    const key = `${sku}||${geography}`;
    const numValue = parseInt(value, 10) || 0;

    // Get original value from report data
    const originalRow = reportData?.rows?.find(
      r => r.sku === sku && r.geography === geography && r.row_type === 'geography'
    );
    const originalValue = originalRow?.proposed_transfer || 0;

    setEditedTransfers(prev => {
      const newEdits = { ...prev };
      if (numValue === originalValue) {
        // If value matches original, remove from edited
        delete newEdits[key];
      } else {
        newEdits[key] = { value: numValue, warehouse_code: warehouseCode };
      }
      return newEdits;
    });
  }, [reportData?.rows]);

  // Save proposed transfers
  const handleSaveTransfers = useCallback(async () => {
    if (!selectedBrand?.id || !hasUnsavedChanges) return;

    setSaving(true);
    try {
      const transfers = Object.entries(editedTransfers).map(([key, data]) => {
        const [sku, geography] = key.split('||');
        return {
          sku,
          geography,
          warehouse_code: data.warehouse_code,
          transfer_qty: data.value,
        };
      });

      await axios.post('/api/inventory-master/proposed-transfers', {
        brand_id: selectedBrand.id,
        transfers,
      });

      // Clear edits and refresh data
      setEditedTransfers({});
      await fetchReportData();
    } catch (err) {
      console.error('Error saving transfers:', err);
      setError(err.response?.data?.error || 'Failed to save transfers');
    } finally {
      setSaving(false);
    }
  }, [selectedBrand?.id, hasUnsavedChanges, editedTransfers, fetchReportData]);

  // Get effective proposed transfer value (edited or original)
  const getEffectiveTransferValue = useCallback((sku, geography, originalValue) => {
    const key = `${sku}||${geography}`;
    if (editedTransfers[key] !== undefined) {
      return editedTransfers[key].value;
    }
    return originalValue || 0;
  }, [editedTransfers]);

  // Calculate SOH after transfer with edited values
  const calculateSohAfterTransfer = useCallback((row) => {
    const proposedTransfer = getEffectiveTransferValue(row.sku, row.geography, row.proposed_transfer);
    return (row.main_branch_soh || 0) + (row.incoming_transfer || 0) + (row.incoming_po || 0) + proposedTransfer;
  }, [getEffectiveTransferValue]);

  // Calculate ROD after transfer with edited values
  const calculateRodAfterTransfer = useCallback((row) => {
    const sohAfterTransfer = calculateSohAfterTransfer(row);
    const dMax = row.d_max || 0;
    if (dMax <= 0) return 9999;
    return Math.round(sohAfterTransfer / dMax);
  }, [calculateSohAfterTransfer]);

  // Transform flat data for table display - add separator rows after each product Total
  const tableData = useMemo(() => {
    if (!reportData?.rows) return [];

    // Optionally drop dead SKUs: those whose Total row has zero stock, velocity,
    // Amazon inventory, inbound and monthly rate everywhere. They pad the grid with
    // rows a planner never acts on. Toggle off to see the full catalog.
    let sourceRows = reportData.rows;
    if (hideZeroSkus) {
      const zeroSkus = new Set(
        reportData.rows
          .filter(r => r.row_type === 'total'
            && !r.main_branch_soh && !r.d30_daily && !r.d14_daily && !r.d7_daily
            && !r.amazon_inventory && !r.incoming_transfer && !r.incoming_po && !r.monthly_avg_rate
            && !r.open_reservations && !r.proposed_transfer)
          .map(r => r.sku)
      );
      sourceRows = reportData.rows.filter(r => !zeroSkus.has(r.sku));
    }

    // Insert separator rows after each 'total' row to visually separate products
    const rowsWithSeparators = [];
    sourceRows.forEach((row, index) => {
      rowsWithSeparators.push(row);

      // Add separator row after each 'total' row (but not at the very end)
      if (row.row_type === 'total' && index < sourceRows.length - 1) {
        // Check if next row is not a category_header (we want separator before category headers too)
        const nextRow = sourceRows[index + 1];
        if (nextRow && nextRow.row_type !== 'category_header') {
          rowsWithSeparators.push({
            row_type: 'separator',
            sku: `separator_${index}`,
            geography: '',
            brand: '',
            name: '',
            category: '',
          });
        }
      }
    });

    return rowsWithSeparators;
  }, [reportData, hideZeroSkus]);

  // Define columns matching the Excel format
  const columns = useMemo(() => [
    {
      id: 'image',
      header: '',
      accessorKey: 'image_url',
      size: 50,
      minSize: 50,
      maxSize: 60,
      enableSorting: false,
      enableResizing: false,
      cell: ({ getValue, row }) => {
        const imageUrl = getValue();
        if (row.original.row_type === 'category_header') return null;
        if (!imageUrl) return null;
        return (
          <Box
            component="img"
            src={imageUrl}
            alt=""
            sx={{
              width: 36,
              height: 36,
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid #e0e0e0',
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        );
      },
    },
    {
      id: 'brand',
      header: 'Brand',
      accessorKey: 'brand',
      size: 100,
      minSize: 60,
      enableSorting: false,
    },
    {
      id: 'sku_type',
      header: 'SKU Type',
      accessorKey: 'sku_type',
      size: 90,
      enableSorting: false,
    },
    {
      id: 'revenue_share',
      header: 'Revenue Share',
      accessorKey: 'revenue_share',
      size: 100,
      align: 'right',
      meta: { clickable: true },
      cell: ({ getValue, row }) => {
        const value = getValue();
        if (row.original.row_type === 'category_header') return '';
        return value ? `${value.toFixed(2)}%` : '0.00%';
      },
    },
    {
      id: 'category',
      header: 'Category',
      accessorKey: 'category',
      size: 120,
      enableSorting: false,
    },
    {
      id: 'name',
      header: 'NAME',
      accessorKey: 'name',
      size: 250,
      enableSorting: true,
    },
    {
      id: 'sku',
      header: 'SKU',
      accessorKey: 'sku',
      size: 150,
      cell: ({ getValue, row }) => {
        // For total rows, show GTIN instead of SKU
        if (row.original.row_type === 'total' && row.original.gtin) {
          return row.original.gtin;
        }
        return getValue();
      },
    },
    {
      id: 'geography',
      header: 'WH',
      accessorKey: 'geography',
      size: 60,
      align: 'center',
    },
    // ========== STOCK COLUMNS (light blue background) ==========
    {
      id: 'main_branch_soh',
      header: 'Main Branch (SOH)',
      accessorKey: 'main_branch_soh',
      size: 120,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#e3f2fd' }, // Light blue
      cell: ({ getValue, row }) => {
        const value = getValue();
        if (row.original.row_type === 'category_header') return '';
        return formatNumber(value);
      },
    },
    {
      id: 'main_branch_available',
      header: 'MAIN BRANCH (Stks available)',
      accessorKey: 'main_branch_available',
      size: 150,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#e3f2fd' }, // Light blue
      cell: ({ getValue, row }) => {
        const value = getValue();
        if (row.original.row_type === 'category_header') return '';
        return formatNumber(value);
      },
    },
    {
      id: 'amazon_inventory',
      header: 'AMAZON Inventory',
      accessorKey: 'amazon_inventory',
      size: 120,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#e3f2fd' }, // Light blue
      cell: ({ getValue, row }) => {
        const value = getValue();
        if (row.original.row_type === 'category_header') return '';
        return formatNumber(value);
      },
    },
    {
      id: 'open_reservations',
      header: 'OPEN ORDERS & RESERVATIONS',
      accessorKey: 'open_reservations',
      size: 120,
      align: 'right',
      headerStyle: { backgroundColor: '#e3f2fd' }, // Light blue
      cell: ({ getValue, row }) => {
        const value = getValue();
        if (row.original.row_type === 'category_header') return '';
        return formatNumber(value);
      },
    },
    // ========== INVENTORY CHECK COLUMNS (light yellow background) ==========
    {
      id: 'first_check_date',
      header: 'First Check',
      accessorKey: 'first_check_date',
      size: 100,
      align: 'center',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#fffde7' }, // Light yellow
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        return formatDateOnly(getValue());
      },
    },
    {
      id: 'first_check_qty',
      header: '1st Qty',
      accessorKey: 'first_check_qty',
      size: 80,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#fffde7' }, // Light yellow
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        return formatNumber(getValue());
      },
    },
    {
      id: 'last_check_date',
      header: 'Last Check',
      accessorKey: 'last_check_date',
      size: 100,
      align: 'center',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#fffde7' }, // Light yellow
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        return formatDateOnly(getValue());
      },
    },
    {
      id: 'last_check_qty',
      header: 'Last Qty',
      accessorKey: 'last_check_qty',
      size: 80,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#fffde7' }, // Light yellow
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        return formatNumber(getValue());
      },
    },
    {
      id: 'total_counts',
      header: '# Counts',
      accessorKey: 'total_counts',
      size: 80,
      align: 'right',
      headerStyle: { backgroundColor: '#fffde7' }, // Light yellow
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        const val = getValue();
        return val > 0 ? formatNumber(val) : '-';
      },
    },
    // ========== RUN RATE COLUMNS (light purple background) ==========
    {
      id: 'd_max',
      header: 'D MAX',
      accessorKey: 'd_max',
      size: 70,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#f3e5f5' }, // Light purple
      cell: ({ getValue, row }) => {
        const value = getValue();
        if (row.original.row_type === 'category_header') return '';
        return formatNumber(value);
      },
    },
    {
      id: 'd30_daily',
      header: 'D30 DAILY (D2C)',
      accessorKey: 'd30_daily',
      size: 100,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#f3e5f5' }, // Light purple
      cell: ({ getValue, row }) => {
        const value = getValue();
        if (row.original.row_type === 'category_header') return '';
        return formatDecimal(value);
      },
    },
    {
      id: 'planning_daily_velocity',
      header: 'Planning Vel',
      accessorKey: 'planning_daily_velocity',
      size: 95,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#ede7f6' },
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        return formatDecimal(getValue());
      },
    },
    {
      id: 'velocity_confidence',
      header: 'Vel Conf',
      accessorKey: 'velocity_confidence',
      size: 85,
      align: 'center',
      headerStyle: { backgroundColor: '#ede7f6' },
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        return getValue() || 'UNKNOWN';
      },
    },
    {
      id: 'velocity_reason',
      header: 'Vel Reason',
      accessorKey: 'velocity_reason',
      size: 140,
      headerStyle: { backgroundColor: '#ede7f6' },
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        return getValue() || 'insufficient_data';
      },
    },
    {
      id: 'd14_daily',
      header: 'D14 DAILY',
      accessorKey: 'd14_daily',
      size: 80,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#f3e5f5' }, // Light purple
      cell: ({ getValue, row }) => {
        const value = getValue();
        if (row.original.row_type === 'category_header') return '';
        return formatDecimal(value);
      },
    },
    {
      id: 'd7_daily',
      header: 'D7 DAILY',
      accessorKey: 'd7_daily',
      size: 80,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#f3e5f5' }, // Light purple
      cell: ({ getValue, row }) => {
        const value = getValue();
        if (row.original.row_type === 'category_header') return '';
        return formatDecimal(value);
      },
    },
    {
      id: 'rod_l30',
      header: 'ROD L30 DAYS',
      accessorKey: 'rod_l30',
      size: 100,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#f3e5f5' }, // Light purple
      cell: ({ getValue, row }) => {
        const value = getValue();
        if (row.original.row_type === 'category_header') return '';
        if (value >= 9999) return 'N/A';
        return formatNumber(value);
      },
    },
    {
      id: 'rod_date',
      header: 'ROD L30 DATE',
      accessorKey: 'rod_date',
      size: 100,
      align: 'center',
      headerStyle: { backgroundColor: '#f3e5f5' }, // Light purple
    },
    {
      id: 'pct_soh_region',
      header: '% SOH by region',
      accessorKey: 'pct_soh_region',
      size: 100,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#f3e5f5' }, // Light purple
      cell: ({ getValue, row }) => {
        const value = getValue();
        if (row.original.row_type === 'category_header') return '';
        return value ? `${value}%` : '0%';
      },
    },
    // ========== TRANSFER COLUMNS (light cyan background) ==========
    {
      id: 'incoming_transfer',
      header: 'Incoming (Transit)',
      accessorKey: 'incoming_transfer',
      size: 110,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#e0f7fa' }, // Light cyan
      cell: ({ getValue, row }) => {
        const value = getValue();
        if (row.original.row_type === 'category_header') return '';
        return formatNumber(value);
      },
    },
    {
      id: 'incoming_po',
      header: 'Incoming (PO)',
      accessorKey: 'incoming_po',
      size: 100,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#e0f7fa' }, // Light cyan
      cell: ({ getValue, row }) => {
        const value = getValue();
        if (row.original.row_type === 'category_header') return '';
        return formatNumber(value);
      },
    },
    {
      id: 'proposed_transfer',
      header: 'Proposed Transfer',
      accessorKey: 'proposed_transfer',
      size: 120,
      align: 'right',
      meta: { clickable: true, editable: true },
      headerStyle: { backgroundColor: '#e0f7fa' }, // Light cyan
      cell: ({ getValue, row, table }) => {
        if (row.original.row_type === 'category_header') return '';
        if (row.original.row_type === 'total') {
          // Total row: show sum of edits for this SKU
          const sku = row.original.sku;
          let totalEdited = 0;
          Object.entries(editedTransfers).forEach(([key, data]) => {
            if (key.startsWith(`${sku}||`)) {
              totalEdited += data.value;
            }
          });
          // If no edits, use original value
          const displayValue = Object.keys(editedTransfers).some(k => k.startsWith(`${sku}||`))
            ? totalEdited
            : getValue() || 0;
          return formatNumber(displayValue);
        }
        // Geography row: editable
        const originalValue = getValue() || 0;
        const editKey = `${row.original.sku}||${row.original.geography}`;
        const editedValue = editedTransfers[editKey]?.value;
        const currentValue = editedValue !== undefined ? editedValue : originalValue;
        const isEdited = editedValue !== undefined;

        return (
          <TextField
            type="number"
            size="small"
            value={currentValue}
            onChange={(e) => handleTransferEdit(
              row.original.sku,
              row.original.geography,
              row.original.warehouse_code,
              e.target.value
            )}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.target.select()}
            sx={{
              width: 80,
              '& .MuiInputBase-input': {
                padding: '4px 8px',
                fontSize: '12px',
                textAlign: 'right',
                backgroundColor: isEdited ? '#fff3e0' : 'transparent',
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: isEdited ? '#ff9800' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: '#ff9800',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#f57c00',
                },
              },
            }}
            inputProps={{ style: { textAlign: 'right' } }}
          />
        );
      },
    },
    {
      id: 'soh_after_transfer',
      header: 'SOH After Transfer',
      accessorKey: 'soh_after_transfer',
      size: 120,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#e0f7fa' }, // Light cyan
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        if (row.original.row_type === 'total') {
          // Recalculate total with edits
          const sku = row.original.sku;
          let totalSohAfterTransfer = 0;
          const geoRows = reportData?.rows?.filter(r => r.sku === sku && r.row_type === 'geography') || [];
          geoRows.forEach(geoRow => {
            totalSohAfterTransfer += calculateSohAfterTransfer(geoRow);
          });
          return formatNumber(totalSohAfterTransfer);
        }
        // Geography row: recalculate with edits
        const calculatedValue = calculateSohAfterTransfer(row.original);
        return formatNumber(calculatedValue);
      },
    },
    {
      id: 'rod_after_transfer',
      header: 'ROD After Transfer',
      accessorKey: 'rod_after_transfer',
      size: 110,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#e0f7fa' }, // Light cyan
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        if (row.original.row_type === 'total') {
          // Recalculate total ROD after transfer
          const sku = row.original.sku;
          let totalSohAfterTransfer = 0;
          const geoRows = reportData?.rows?.filter(r => r.sku === sku && r.row_type === 'geography') || [];
          geoRows.forEach(geoRow => {
            totalSohAfterTransfer += calculateSohAfterTransfer(geoRow);
          });
          const totalDMax = row.original.d_max || 0;
          const totalRod = totalDMax > 0 ? Math.round(totalSohAfterTransfer / totalDMax) : 9999;
          if (totalRod >= 9999) return 'N/A';
          return formatNumber(totalRod);
        }
        // Geography row: recalculate with edits
        const calculatedValue = calculateRodAfterTransfer(row.original);
        if (calculatedValue >= 9999) return 'N/A';
        return formatNumber(calculatedValue);
      },
    },
    // ========== SIMULATION COLUMNS (12-month projection) ==========
    {
      id: 'monthly_avg_rate',
      header: '3Mo Avg Monthly',
      accessorKey: 'monthly_avg_rate',
      size: 100,
      align: 'right',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#c8e6c9' }, // Light green (simulation)
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        const value = getValue();
        return formatNumber(value);
      },
    },
    {
      id: 'expiry_alert',
      header: 'Expiry Alert',
      accessorKey: 'expiry_alert',
      size: 90,
      align: 'center',
      meta: { clickable: true },
      headerStyle: { backgroundColor: '#ffccbc' }, // Light orange (expiry)
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        const value = getValue();
        if (!value) return '';
        const colorMap = {
          '<6 Months': { bg: '#ffcdd2', color: '#c62828' },
          '7-12 Months': { bg: '#fff3e0', color: '#e65100' },
          '13-18 Months': { bg: '#e3f2fd', color: '#1565c0' },
        };
        const style = colorMap[value] || {};
        return (
          <Box sx={{
            backgroundColor: style.bg,
            color: style.color,
            padding: '2px 6px',
            borderRadius: 1,
            fontSize: '11px',
            fontWeight: 600,
          }}>
            {value}
          </Box>
        );
      },
    },
    // Dynamic simulation columns - WITHOUT proposal (green background)
    ...(reportData?.simulation_months || []).map((month) => ({
      id: `sim_without_${month}`,
      header: `WH INV ${month}`,
      accessorFn: (row) => row.simulation_without_proposal?.[month],
      size: 85,
      align: 'right',
      meta: { clickable: true, simulation: 'without' },
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        const value = getValue();
        const isNegative = value < 0;
        return (
          <Box sx={{ color: isNegative ? '#c62828' : 'inherit' }}>
            {formatNumber(value)}
          </Box>
        );
      },
      headerStyle: { backgroundColor: '#c8e6c9' }, // Light green for WITHOUT proposal
    })),
    // Dynamic simulation columns - WITH proposal (yellow background)
    ...(reportData?.simulation_months || []).map((month) => ({
      id: `sim_with_${month}`,
      header: `WH+PO ${month}`,
      accessorFn: (row) => row.simulation_with_proposal?.[month],
      size: 85,
      align: 'right',
      meta: { clickable: true, simulation: 'with' },
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        const value = getValue();
        const isNegative = value < 0;
        return (
          <Box sx={{ color: isNegative ? '#c62828' : 'inherit' }}>
            {formatNumber(value)}
          </Box>
        );
      },
      headerStyle: { backgroundColor: '#fff9c4' }, // Light yellow for WITH proposal
    })),
    // ========== EXPIRY COLUMNS (monthly expiring quantities) ==========
    ...(reportData?.simulation_months || []).slice(0, 12).map((month) => ({
      id: `expiry_${month}`,
      header: `Expiry ${month}`,
      accessorFn: (row) => row.monthly_expiry?.[month],
      size: 80,
      align: 'right',
      meta: { clickable: true, expiry: true },
      cell: ({ getValue, row }) => {
        if (row.original.row_type === 'category_header') return '';
        const value = getValue() || 0;
        if (value === 0) return '';
        return (
          <Box sx={{ color: '#c62828', fontWeight: value > 0 ? 600 : 400 }}>
            {formatNumber(value)}
          </Box>
        );
      },
      headerStyle: { backgroundColor: '#ffccbc' }, // Light orange for expiry
    })),
  ], [editedTransfers, handleTransferEdit, reportData, calculateSohAfterTransfer, calculateRodAfterTransfer]);

  // Format helpers
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    const num = Number(value);
    if (isNaN(num)) return '0';
    return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const formatDecimal = (value) => {
    if (value === null || value === undefined) return '0.00';
    const num = Number(value);
    if (isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDateOnly = (value) => {
    if (!value) return '-';
    // Handle both ISO format (2024-12-04T23:59:59) and date-only format (2024-12-04)
    const dateStr = String(value).split('T')[0].split(' ')[0];
    return dateStr || '-';
  };

  // Get row styling based on row type
  const getRowStyle = (row) => {
    const rowType = row.original.row_type;

    if (rowType === 'category_header') {
      return {
        backgroundColor: '#F4A460', // Sandy brown / orange for category headers
        fontWeight: 700,
        '& td': {
          fontWeight: 700,
          color: '#000',
        },
      };
    }

    if (rowType === 'total') {
      return {
        backgroundColor: '#FFFACD', // Light yellow for total rows
        fontWeight: 700,
        '& td': {
          fontWeight: 700,
        },
      };
    }

    if (rowType === 'separator') {
      return {
        backgroundColor: '#f0f0f0', // Light gray for separator rows
        height: '12px',
        '& td': {
          padding: '0',
          borderBottom: 'none',
        },
      };
    }

    return {};
  };

  // Get cell styling for negative values
  const getCellStyle = (value, rowType) => {
    if (rowType === 'category_header') return {};

    const numValue = Number(value);
    if (!isNaN(numValue) && numValue < 0) {
      return { color: '#c62828' }; // Red for negative values
    }
    return {};
  };

  // Stock columns that should open the stock breakdown dialog
  const STOCK_BREAKDOWN_COLUMNS = ['main_branch_soh', 'main_branch_available', 'amazon_inventory'];

  // Handle calculation popup or stock breakdown dialog
  const handleCalcClick = (event, field, row) => {
    if (row.row_type === 'category_header') return;
    if (!CALCULATION_INFO[field]) return;
    event.stopPropagation();

    // For stock columns, open the stock breakdown dialog instead
    if (STOCK_BREAKDOWN_COLUMNS.includes(field)) {
      // Use warehouse_code from the row data (e.g., DRG, ITC, SYN)
      // Not geography (US, UK, EU) which doesn't match stock levels API
      setStockBreakdownDialog({
        open: true,
        sku: row.sku,
        warehouseCode: row.warehouse_code,
        productName: row.name,
      });
      return;
    }

    // For other columns, open the regular calculation popover
    setCalcPopover({ anchorEl: event.currentTarget, field, row });
  };

  const handleCalcClose = () => {
    setCalcPopover({ anchorEl: null, field: null, row: null });
  };

  // Get actual values for the calculation display
  const getCalcValues = (field, row) => {
    if (!row) return [];
    const values = [];
    switch (field) {
      case 'd_max':
        values.push({ label: 'D7 Daily', value: formatDecimal(row.d7_daily) });
        values.push({ label: 'D14 Daily', value: formatDecimal(row.d14_daily) });
        values.push({ label: 'D30 Daily', value: formatDecimal(row.d30_daily) });
        values.push({ label: 'D Max (Result)', value: formatDecimal(row.d_max), highlight: true });
        break;
      case 'rod_l30':
        values.push({ label: 'Stock on Hand', value: formatNumber(row.main_branch_soh) });
        values.push({ label: 'D30 Daily Rate', value: formatDecimal(row.d30_daily) });
        values.push({ label: 'ROD Days (Result)', value: row.rod_l30 >= 9999 ? 'N/A' : formatNumber(row.rod_l30), highlight: true });
        break;
      case 'revenue_share':
        values.push({ label: 'Revenue Share', value: `${(row.revenue_share || 0).toFixed(2)}%`, highlight: true });
        break;
      case 'd7_daily':
      case 'd14_daily':
      case 'd30_daily':
        values.push({ label: 'Run Rate', value: formatDecimal(row[field]), highlight: true });
        break;
      case 'planning_daily_velocity':
        values.push({ label: 'Observed D30 Daily', value: formatDecimal(row.observed_daily_velocity ?? row.d30_daily) });
        values.push({ label: 'Adjusted Daily Velocity', value: formatDecimal(row.adjusted_daily_velocity) });
        values.push({ label: 'Confidence', value: row.velocity_confidence || 'UNKNOWN' });
        values.push({ label: 'Reason', value: row.velocity_reason || 'insufficient_data' });
        if (row.velocity_explanation) {
          values.push({ label: 'Explanation', value: row.velocity_explanation });
        }
        values.push({ label: 'Planning Velocity (Result)', value: formatDecimal(row.planning_daily_velocity), highlight: true });
        break;
      case 'main_branch_soh':
        values.push({ label: 'Stock on Hand', value: formatNumber(row.main_branch_soh), highlight: true });
        break;
      case 'main_branch_available':
        values.push({ label: 'Stock on Hand (SOH)', value: formatNumber(row.main_branch_soh) });
        values.push({ label: 'Reserved (Retailer)', value: '0' }); // Will show actual when data available
        values.push({ label: 'Expiring (<6 months)', value: '0' }); // Will show actual when data available
        values.push({ label: 'Available (Result)', value: formatNumber(row.main_branch_available), highlight: true });
        break;
      case 'amazon_inventory':
        values.push({ label: 'Amazon Inventory', value: formatNumber(row.amazon_inventory), highlight: true });
        break;
      case 'pct_soh_region':
        values.push({ label: '% of Total SKU Stock', value: `${row.pct_soh_region || 0}%`, highlight: true });
        break;
      case 'first_check_date':
        values.push({ label: 'First Physical Count Date', value: row.first_check_date || 'N/A', highlight: true });
        break;
      case 'first_check_qty':
        values.push({ label: 'Quantity at First Check', value: formatNumber(row.first_check_qty), highlight: true });
        break;
      case 'last_check_date':
        values.push({ label: 'Last Physical Count Date', value: row.last_check_date || 'N/A', highlight: true });
        break;
      case 'last_check_qty':
        values.push({ label: 'Quantity at Last Check', value: formatNumber(row.last_check_qty), highlight: true });
        break;
      case 'total_counts':
        values.push({ label: 'Total Physical Counts', value: formatNumber(row.total_counts), highlight: true });
        values.push({ label: 'First Count Date', value: row.first_check_date || 'N/A' });
        values.push({ label: 'Last Count Date', value: row.last_check_date || 'N/A' });
        break;
      case 'incoming_transfer':
        values.push({ label: 'In-Transit Transfers', value: formatNumber(row.incoming_transfer), highlight: true });
        break;
      case 'incoming_po':
        values.push({ label: 'Approved/Sent POs', value: formatNumber(row.incoming_po), highlight: true });
        break;
      case 'proposed_transfer':
        values.push({ label: 'Proposed Transfer', value: formatNumber(getEffectiveTransferValue(row.sku, row.geography, row.proposed_transfer)), highlight: true });
        break;
      case 'soh_after_transfer':
        values.push({ label: 'Stock on Hand (SOH)', value: formatNumber(row.main_branch_soh) });
        values.push({ label: 'Incoming (Transit)', value: formatNumber(row.incoming_transfer) });
        values.push({ label: 'Incoming (PO)', value: formatNumber(row.incoming_po) });
        values.push({ label: 'Proposed Transfer', value: formatNumber(getEffectiveTransferValue(row.sku, row.geography, row.proposed_transfer)) });
        values.push({ label: 'SOH After Transfer (Result)', value: formatNumber(calculateSohAfterTransfer(row)), highlight: true });
        break;
      case 'rod_after_transfer':
        values.push({ label: 'SOH After Transfer', value: formatNumber(calculateSohAfterTransfer(row)) });
        values.push({ label: 'D Max Rate', value: formatDecimal(row.d_max) });
        const rodAfter = calculateRodAfterTransfer(row);
        values.push({ label: 'ROD After (Result)', value: rodAfter >= 9999 ? 'N/A' : formatNumber(rodAfter), highlight: true });
        break;
      case 'monthly_avg_rate':
        values.push({ label: '3-Month Avg Monthly Rate', value: formatNumber(row.monthly_avg_rate), highlight: true });
        values.push({ label: 'Calculation', value: 'Sum(90-day sales) / 3' });
        break;
      case 'expiry_alert':
        values.push({ label: 'Expiry Alert', value: row.expiry_alert || 'None', highlight: true });
        values.push({ label: 'Based on', value: 'Earliest batch expiry date' });
        break;
      default:
        break;
    }
    return values;
  };

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      globalFilter,
      columnSizing,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    // Search also matches every alias / UPC / ASIN of the SKU (row.search_terms from
    // the API), not only the visible cells (Rishabh 2026-10-14).
    globalFilterFn: (row, columnId, filterValue) => {
      const q = String(filterValue ?? '').toLowerCase();
      if (!q) return true;
      const cell = row.getValue(columnId);
      if (cell != null && String(cell).toLowerCase().includes(q)) return true;
      return String(row.original.search_terms || '').toLowerCase().includes(q);
    },
    onColumnSizingChange: setColumnSizing,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // Virtualizer for performance - only render visible rows
  const { rows } = table.getRowModel();
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 32, // Estimated row height in pixels
    overscan: 10, // Render 10 extra rows above/below viewport
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  // Export to Excel
  const handleExport = () => {
    if (!tableData || tableData.length === 0) return;

    const simulationMonths = reportData?.simulation_months || [];

    // Filter out separator rows - they're only for visual display
    const exportData = tableData.filter(row => row.row_type !== 'separator').map(row => {
      // Calculate values with any edits applied
      const effectiveProposedTransfer = getEffectiveTransferValue(row.sku, row.geography, row.proposed_transfer);
      const calculatedSohAfterTransfer = row.row_type === 'geography' ? calculateSohAfterTransfer(row) : row.soh_after_transfer;
      const calculatedRodAfterTransfer = row.row_type === 'geography' ? calculateRodAfterTransfer(row) : row.rod_after_transfer;

      // Base columns
      const baseData = {
        'Brand': row.brand,
        'SKU Type': row.sku_type,
        'Revenue Share': row.revenue_share ? `${row.revenue_share.toFixed(2)}%` : '0.00%',
        'Category': row.category,
        'NAME': row.name,
        'SKU': row.row_type === 'total' && row.gtin ? row.gtin : row.sku,
        'WH': row.geography,
        'Main Branch (SOH)': row.main_branch_soh,
        'MAIN BRANCH (Stks available)': row.main_branch_available,
        'AMAZON Inventory': row.amazon_inventory,
        'OPEN ORDERS & RESERVATIONS': row.open_reservations,
        // Inventory check columns (before run rates)
        'First Check': row.first_check_date || '',
        '1st Qty': row.first_check_qty || 0,
        'Last Check': row.last_check_date || '',
        'Last Qty': row.last_check_qty || 0,
        '# Counts': row.total_counts || 0,
        // Run rate columns
        'D MAX': row.d_max,
        'D30 DAILY (D2C)': row.d30_daily,
        'Planning Vel': row.planning_daily_velocity,
        'Velocity Confidence': row.velocity_confidence || '',
        'Velocity Reason': row.velocity_reason || '',
        'D14 DAILY': row.d14_daily,
        'D7 DAILY': row.d7_daily,
        'ROD L30 DAYS': row.rod_l30 >= 9999 ? 'N/A' : row.rod_l30,
        'ROD L30 DATE': row.rod_date,
        '% SOH by region': row.pct_soh_region ? `${row.pct_soh_region}%` : '0%',
        // Transfer columns
        'Incoming (Transit)': row.incoming_transfer || 0,
        'Incoming (PO)': row.incoming_po || 0,
        'Proposed Transfer': effectiveProposedTransfer,
        'SOH After Transfer': calculatedSohAfterTransfer,
        'ROD After Transfer': calculatedRodAfterTransfer >= 9999 ? 'N/A' : calculatedRodAfterTransfer,
        // Simulation base columns
        '3Mo Avg Monthly': row.monthly_avg_rate || 0,
        'Expiry Alert': row.expiry_alert || '',
      };

      // Add simulation columns WITHOUT proposal (green)
      simulationMonths.forEach(month => {
        baseData[`WH INV ${month}`] = row.simulation_without_proposal?.[month] ?? '';
      });

      // Add simulation columns WITH proposal (yellow)
      simulationMonths.forEach(month => {
        baseData[`WH+PO ${month}`] = row.simulation_with_proposal?.[month] ?? '';
      });

      // Add expiry columns
      simulationMonths.forEach(month => {
        baseData[`Expiry ${month}`] = row.monthly_expiry?.[month] || '';
      });

      return baseData;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MSR');

    const fileName = `msr_${selectedBrand?.name || 'export'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  if (!brands || brands.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No brands available. Please create a brand first.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header with Brand Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={3} alignItems="center">
            <Business sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                MSR (Master Stock Report)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                SKU run rates by geography with bundle explosion and revenue share calculation
              </Typography>
            </Box>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>Select Brand</InputLabel>
              <Select
                value={selectedBrand?.id || ''}
                onChange={handleBrandChange}
                label="Select Brand"
              >
                {brands.map(brand => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {!selectedBrand ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please select a brand above to view MSR data
        </Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : (
        <>
          {/* Summary Cards */}
          {reportData?.summary && (
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Paper sx={{ p: 2, flex: 1 }}>
                <Typography variant="body2" color="text.secondary">Total SKUs</Typography>
                <Typography variant="h4">{reportData.summary.total_skus}</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1 }}>
                <Typography variant="body2" color="text.secondary">Total Units</Typography>
                <Typography variant="h4">{reportData.summary.total_units?.toLocaleString() || 0}</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1 }}>
                <Typography variant="body2" color="text.secondary">Categories</Typography>
                <Typography variant="h4">{reportData.summary.total_categories}</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1 }}>
                <Typography variant="body2" color="text.secondary">As Of Date</Typography>
                <Typography variant="h6">{reportData.as_of_date}</Typography>
              </Paper>
            </Stack>
          )}

          {/* Warn about stock excluded from Main Branch SOH (unmapped country or
              non-sellable warehouse) so it is not silently invisible. */}
          {Array.isArray(reportData?.warnings) && reportData.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Stock excluded from Main Branch SOH:{' '}
              {reportData.warnings[0].warehouses
                .map(w => `${w.warehouse_code} (${w.units.toLocaleString()} units, ${w.reason === 'OTHER_GEO' ? 'unmapped country' : 'not sellable'})`)
                .join(', ')}
            </Alert>
          )}

          {/* Data Table */}
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            {/* Toolbar */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h6">
                MSR Data
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={globalFilter ?? ''}
                  onChange={e => setGlobalFilter(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 180, maxWidth: 260 }}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={hideZeroSkus} onChange={e => setHideZeroSkus(e.target.checked)} />}
                  label="Hide zero SKUs"
                  // ml:2 overrides MUI's default -11px marginLeft on FormControlLabel, which
                  // otherwise pulls the switch's hit-area left under the Search box; flexShrink:0
                  // keeps it from being squeezed there when the toolbar is tight.
                  sx={{ whiteSpace: 'nowrap', ml: 2, mr: 0, flexShrink: 0 }}
                />
                {/* Save Button - highlighted when there are unsaved changes */}
                <Button
                  variant={hasUnsavedChanges ? "contained" : "outlined"}
                  color={hasUnsavedChanges ? "warning" : "primary"}
                  size="small"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                  onClick={handleSaveTransfers}
                  disabled={!hasUnsavedChanges || saving}
                  sx={{
                    minWidth: 100,
                    animation: hasUnsavedChanges ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { boxShadow: '0 0 0 0 rgba(255, 152, 0, 0.4)' },
                      '70%': { boxShadow: '0 0 0 10px rgba(255, 152, 0, 0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(255, 152, 0, 0)' },
                    },
                  }}
                >
                  {saving ? 'Saving...' : hasUnsavedChanges ? 'Save Changes' : 'Save'}
                </Button>
                <Tooltip title="Export to Excel">
                  <IconButton onClick={handleExport} size="small">
                    <FileDownload />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh Data">
                  <IconButton onClick={fetchReportData} size="small">
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {/* Table Container - Virtualized for performance */}
            <Box
              ref={tableContainerRef}
              sx={{ maxHeight: 'calc(100vh - 400px)', overflow: 'auto' }}
            >
              <table
                style={{
                  width: table.getTotalSize(),
                  minWidth: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '12px',
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  tableLayout: 'fixed',
                }}
              >
                <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          style={{
                            padding: '8px 12px',
                            textAlign: header.column.columnDef.align || 'left',
                            backgroundColor: header.column.columnDef.headerStyle?.backgroundColor || '#f5f5f5',
                            borderBottom: '2px solid #ddd',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            fontWeight: 600,
                            cursor: header.column.getCanSort() ? 'pointer' : 'default',
                            width: header.getSize(),
                            minWidth: header.column.columnDef.minSize || 40,
                            verticalAlign: 'bottom',
                          }}
                        >
                          <div style={{ position: 'relative' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-end',
                              justifyContent: header.column.columnDef.align === 'right' ? 'flex-end' :
                                             header.column.columnDef.align === 'center' ? 'center' : 'flex-start',
                              pr: header.column.columnDef.enableResizing !== false ? 2 : 0,
                              minHeight: '32px',
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {header.column.getCanSort() && (
                              <Box sx={{ ml: 0.5, display: 'flex', alignItems: 'center' }}>
                                {header.column.getIsSorted() === 'asc' ? (
                                  <ArrowUpward sx={{ fontSize: 16 }} />
                                ) : header.column.getIsSorted() === 'desc' ? (
                                  <ArrowDownward sx={{ fontSize: 16 }} />
                                ) : (
                                  <UnfoldMore sx={{ fontSize: 16, opacity: 0.3 }} />
                                )}
                              </Box>
                            )}
                          </Box>
                          {/* Column Resize Handle */}
                          {header.column.columnDef.enableResizing !== false && (
                            <Box
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              sx={{
                                position: 'absolute',
                                right: -4,
                                top: 0,
                                bottom: 0,
                                width: '8px',
                                cursor: 'col-resize',
                                userSelect: 'none',
                                touchAction: 'none',
                                backgroundColor: header.column.getIsResizing() ? 'primary.main' : 'transparent',
                                '&:hover': {
                                  backgroundColor: 'primary.light',
                                },
                              }}
                            />
                          )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        style={{ textAlign: 'center', padding: '40px' }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No data available
                        </Typography>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {/* Padding row for virtual scroll positioning */}
                      {virtualRows.length > 0 && virtualRows[0].start > 0 && (
                        <tr style={{ height: virtualRows[0].start }} />
                      )}
                      {virtualRows.map(virtualRow => {
                        const row = rows[virtualRow.index];
                        const rowType = row.original.row_type;
                        const isCategoryHeader = rowType === 'category_header';
                        const isTotal = rowType === 'total';
                        const isSeparator = rowType === 'separator';

                        // Handle separator row - render empty row with minimal height
                        if (isSeparator) {
                          return (
                            <tr key={row.id} style={{ height: '12px', backgroundColor: '#f8f8f8' }}>
                              <td
                                colSpan={columns.length}
                                style={{ padding: 0, borderBottom: '1px solid #e0e0e0' }}
                              />
                            </tr>
                          );
                        }

                        let rowStyle = {
                          borderBottom: '1px solid #eee',
                        };

                        if (isCategoryHeader) {
                          rowStyle = {
                            ...rowStyle,
                            backgroundColor: '#F4A460',
                            fontWeight: 700,
                          };
                        } else if (isTotal) {
                          rowStyle = {
                            ...rowStyle,
                            backgroundColor: '#FFFACD',
                            fontWeight: 700,
                          };
                        }

                        return (
                          <tr key={row.id} style={rowStyle}>
                            {row.getVisibleCells().map(cell => {
                              const columnDef = cell.column.columnDef;
                              const value = cell.getValue();
                              const numValue = Number(value);

                              let cellStyle = {
                                padding: '6px 12px',
                                textAlign: columnDef.align || 'left',
                                borderRight: '1px solid #f0f0f0',
                                fontWeight: isCategoryHeader || isTotal ? 700 : 400,
                                width: cell.column.getSize(),
                                minWidth: cell.column.getSize(),
                                maxWidth: cell.column.getSize(),
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              };

                              // Red color for negative values (except category headers)
                              if (!isCategoryHeader && !isNaN(numValue) && numValue < 0) {
                                cellStyle.color = '#c62828';
                              }

                              const isEditable = columnDef.meta?.editable && row.original.row_type === 'geography';
                              const isClickable = columnDef.meta?.clickable && !isCategoryHeader && !isEditable && CALCULATION_INFO[cell.column.id];

                              return (
                                <td
                                  key={cell.id}
                                  style={{
                                    ...cellStyle,
                                    cursor: isClickable ? 'pointer' : 'default',
                                    overflow: isEditable ? 'visible' : 'hidden',
                                  }}
                                  onClick={isClickable ? (e) => handleCalcClick(e, cell.column.id, row.original) : undefined}
                                >
                                  {isCategoryHeader && cell.column.id === 'category' ? (
                                    <span style={{ fontWeight: 700 }}>
                                      {row.original.category}
                                    </span>
                                  ) : isClickable ? (
                                    <Box
                                      sx={{
                                        display: 'inline',
                                        borderBottom: '1px dashed #999',
                                        '&:hover': { borderBottomColor: 'primary.main', color: 'primary.main' },
                                      }}
                                    >
                                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </Box>
                                  ) : (
                                    flexRender(cell.column.columnDef.cell, cell.getContext())
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                      {/* Bottom padding row for virtual scroll */}
                      {virtualRows.length > 0 && (
                        <tr style={{ height: totalSize - (virtualRows[virtualRows.length - 1]?.end || 0) }} />
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {table.getFilteredRowModel().rows.length} of {tableData.length} rows
              </Typography>
            </Box>
          </Paper>
        </>
      )}

      {/* Calculation Popover */}
      <Popover
        open={Boolean(calcPopover.anchorEl)}
        anchorEl={calcPopover.anchorEl}
        onClose={handleCalcClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{ mt: 1 }}
      >
        {calcPopover.field && CALCULATION_INFO[calcPopover.field] && (
          <Box sx={{ p: 2, maxWidth: 350 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {CALCULATION_INFO[calcPopover.field].title}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {CALCULATION_INFO[calcPopover.field].description}
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Formula
              </Typography>
              <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                {CALCULATION_INFO[calcPopover.field].formula}
              </Typography>
            </Paper>
            {calcPopover.row && (
              <>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Values for {calcPopover.row.sku} ({calcPopover.row.geography})
                </Typography>
                <Stack spacing={0.5}>
                  {getCalcValues(calcPopover.field, calcPopover.row).map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        bgcolor: item.highlight ? 'primary.50' : 'transparent',
                        p: item.highlight ? 0.5 : 0,
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" color={item.highlight ? 'primary.main' : 'text.secondary'}>
                        {item.label}:
                      </Typography>
                      <Typography variant="body2" fontWeight={item.highlight ? 600 : 400}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </Box>
        )}
      </Popover>

      {/* Stock Breakdown Dialog */}
      <StockBreakdownDialog
        open={stockBreakdownDialog.open}
        onClose={() => setStockBreakdownDialog({ ...stockBreakdownDialog, open: false })}
        sku={stockBreakdownDialog.sku}
        warehouseCode={stockBreakdownDialog.warehouseCode}
        productName={stockBreakdownDialog.productName}
        brandId={selectedBrand?.id}
      />
    </Box>
  );
};

export default InventoryRunRateReport;
